import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Alert, Button,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import ComputerIcon from "@mui/icons-material/Computer";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { getSesiones, forzarLogout, forzarLogoutTodos, SesionUsuario } from "../services/admin.service";

/* ─── Parsers ─────────────────────────────────────── */
function parseUA(ua: string | null) {
  if (!ua) return { browser: "?", browserVer: "", os: "?", device: "escritorio" };

  const s = ua;

  const device = /Mobile|Android|iPhone|iPad|iPod/i.test(s) ? "móvil" : "escritorio";

  let browser = "Desconocido";
  let browserVer = "";
  if (/Edg\//i.test(s)) {
    browser = "Edge";
    browserVer = (s.match(/Edg\/([\d.]+)/) || [])[1] || "";
  } else if (/OPR\//i.test(s)) {
    browser = "Opera";
    browserVer = (s.match(/OPR\/([\d.]+)/) || [])[1] || "";
  } else if (/Chrome\//i.test(s)) {
    browser = "Chrome";
    browserVer = (s.match(/Chrome\/([\d.]+)/) || [])[1] || "";
  } else if (/Firefox\//i.test(s)) {
    browser = "Firefox";
    browserVer = (s.match(/Firefox\/([\d.]+)/) || [])[1] || "";
  } else if (/Safari\//i.test(s) && !/Chrome/i.test(s)) {
    browser = "Safari";
    browserVer = (s.match(/Version\/([\d.]+)/) || [])[1] || "";
  }

  let os = "Desconocido";
  if (/Windows NT 10/i.test(s)) os = "Windows 10/11";
  else if (/Windows NT 6\.3/i.test(s)) os = "Windows 8.1";
  else if (/Windows/i.test(s)) os = "Windows";
  else if (/iPhone OS ([\d_]+)/i.test(s)) os = `iOS ${((s.match(/iPhone OS ([\d_]+)/i) || [])[1] || "").replace(/_/g, ".")}`;
  else if (/iPad.*OS ([\d_]+)/i.test(s)) os = `iPadOS ${((s.match(/OS ([\d_]+)/i) || [])[1] || "").replace(/_/g, ".")}`;
  else if (/Android ([\d.]+)/i.test(s)) os = `Android ${(s.match(/Android ([\d.]+)/i) || [])[1] || ""}`;
  else if (/Mac OS X ([\d_]+)/i.test(s)) os = `macOS ${((s.match(/Mac OS X ([\d_]+)/i) || [])[1] || "").replace(/_/g, ".")}`;
  else if (/Linux/i.test(s)) os = "Linux";

  return { browser, browserVer, os, device };
}

function formatTiempo(seg: number) {
  if (seg <= 0) return "Expirado";
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function colorTiempo(seg: number): "success" | "warning" | "error" | "default" {
  if (seg <= 0) return "error";
  if (seg < 3600) return "warning";
  return "success";
}

function formatFecha(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-PE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
}

const REFRESH = 30;

/* ─── Componente ──────────────────────────────────── */
export default function AdminSesionesPage() {
  const [sesiones, setSesiones] = useState<SesionUsuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(REFRESH);
  const [kicking, setKicking] = useState<string | null>(null);
  const [kickingAll, setKickingAll] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setError("");
      setSesiones(await getSesiones());
      setCountdown(REFRESH);
    } catch {
      setError("Error al cargar sesiones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, REFRESH * 1000);
    return () => clearInterval(t);
  }, [cargar]);

  useEffect(() => {
    const t = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  const handleKick = async (id: string, correo: string) => {
    if (!window.confirm(`¿Cerrar sesión de ${correo}?`)) return;
    setKicking(id);
    try { await forzarLogout(id); await cargar(); }
    catch { setError("Error al cerrar la sesión"); }
    finally { setKicking(null); }
  };

  const handleKickAll = async () => {
    const n = activos.length;
    if (!n) return;
    if (!window.confirm(`¿Cerrar las ${n} sesión(es) activa(s)? Tu sesión se mantiene.`)) return;
    setKickingAll(true);
    try {
      const { cantidad } = await forzarLogoutTodos();
      await cargar();
      if (cantidad === 0) setError("No había sesiones activas");
    } catch { setError("Error al cerrar sesiones"); }
    finally { setKickingAll(false); }
  };

  const activos = sesiones.filter(s => s.session_activa && s.segundos_restantes > 0);

  return (
    <Box sx={{ p: 3, background: "linear-gradient(135deg,#0f172a,#1e293b)", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1300, mx: "auto" }}>

        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="white">
              Sesiones y Conexiones
            </Typography>
            <Typography variant="body2" color="grey.400">
              {activos.length} conectado{activos.length !== 1 ? "s" : ""} · {sesiones.length} registro{sesiones.length !== 1 ? "s" : ""} en el historial — actualiza en {countdown}s
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            {activos.length > 0 && (
              <Button variant="contained" color="error" size="small"
                startIcon={kickingAll ? <CircularProgress size={16} color="inherit" /> : <PowerOffIcon />}
                disabled={kickingAll} onClick={handleKickAll}
                sx={{ fontWeight: 700, textTransform: "none" }}>
                Kick All ({activos.length})
              </Button>
            )}
            <Tooltip title="Actualizar">
              <IconButton onClick={cargar} sx={{ color: "white" }}><RefreshIcon /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" pt={6}>
            <CircularProgress sx={{ color: "white" }} />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, background: "rgba(255,255,255,0.97)", overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#0f172a" }}>
                  {["Usuario", "Rol", "Estado", "IP", "Dispositivo / Browser", "Login", "Expira en", "Kick"].map(h => (
                    <TableCell key={h} sx={{ color: "white", fontWeight: 700, whiteSpace: "nowrap", py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sesiones.map(s => {
                  const conectado = s.session_activa && s.segundos_restantes > 0;
                  const esBoss = s.rol === "BOSS";
                  const ua = parseUA(s.user_agent);

                  return (
                    <TableRow key={s.id} hover sx={esBoss ? { background: "rgba(15,23,42,0.05)" } : {}}>

                      {/* Usuario */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography fontWeight={600} fontSize={13}>{s.nombre || "—"}</Typography>
                        <Typography fontSize={11} color="text.secondary">{s.correo}</Typography>
                      </TableCell>

                      {/* Rol */}
                      <TableCell>
                        <Chip label={s.rol} size="small"
                          sx={{ fontWeight: 700, fontSize: 10,
                            background: esBoss ? "#0f172a" : undefined,
                            color: esBoss ? "white" : undefined }} />
                      </TableCell>

                      {/* Estado */}
                      <TableCell>
                        <Chip size="small"
                          label={conectado ? "Conectado" : s.revoked_at ? "Deslogeado" : "Expirada"}
                          color={conectado ? "success" : "default"} />
                      </TableCell>

                      {/* IP */}
                      <TableCell>
                        {s.ip ? (
                          <Tooltip title={`JTI: ${s.jti || "—"}`}>
                            <Typography fontSize={12} fontFamily="monospace"
                              sx={{ cursor: "default", color: conectado ? "text.primary" : "text.disabled" }}>
                              {s.ip}
                            </Typography>
                          </Tooltip>
                        ) : <Typography fontSize={12} color="text.disabled">—</Typography>}
                      </TableCell>

                      {/* Dispositivo / Browser */}
                      <TableCell>
                        {s.user_agent ? (
                          <Tooltip title={s.user_agent} arrow>
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: "default" }}>
                              {ua.device === "móvil"
                                ? <PhoneAndroidIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                                : <ComputerIcon sx={{ fontSize: 15, color: "text.secondary" }} />}
                              <Box>
                                <Typography fontSize={12} fontWeight={600} lineHeight={1.2}>
                                  {ua.browser} {ua.browserVer ? `${ua.browserVer.split(".")[0]}` : ""}
                                </Typography>
                                <Typography fontSize={11} color="text.secondary" lineHeight={1.2}>
                                  {ua.os} · {ua.device}
                                </Typography>
                              </Box>
                            </Box>
                          </Tooltip>
                        ) : <Typography fontSize={12} color="text.disabled">—</Typography>}
                      </TableCell>

                      {/* Login */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography fontSize={12}>{formatFecha(s.created_at)}</Typography>
                      </TableCell>

                      {/* Expira en */}
                      <TableCell>
                        {s.session_activa ? (
                          <Chip size="small" variant="outlined"
                            label={formatTiempo(s.segundos_restantes)}
                            color={colorTiempo(s.segundos_restantes)} />
                        ) : <Typography fontSize={12} color="text.disabled">—</Typography>}
                      </TableCell>

                      {/* Kick */}
                      <TableCell align="center">
                        {conectado && !esBoss ? (
                          <Tooltip title="Cerrar sesión">
                            <span>
                              <IconButton size="small" color="error"
                                disabled={kicking === s.id}
                                onClick={() => handleKick(s.id, s.correo)}>
                                {kicking === s.id
                                  ? <CircularProgress size={16} />
                                  : <LogoutIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : <Typography fontSize={12} color="text.disabled">—</Typography>}
                      </TableCell>

                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
